/**
 * Helper functions for managing autosave preferences
 * Handles localStorage persistence with fallback for edge cases
 */

const AUTOSAVE_STORAGE_KEY = "snap-reports:autosave-enabled";

/**
 * Get the autosave preference from localStorage
 * @returns boolean - true if enabled (or default), false if explicitly disabled
 */
export function getAutosavePreference(): boolean {
  try {
    const stored = localStorage.getItem(AUTOSAVE_STORAGE_KEY);

    // If no preference stored, default to enabled (opt-out model)
    if (stored === null) {
      return true;
    }

    return stored === "true";
  } catch (error) {
    // localStorage might be unavailable (private browsing, storage full, etc.)
    console.warn(
      "[Autosave] Failed to read preference from localStorage:",
      error
    );
    return true; // Default to enabled on error
  }
}

/**
 * Save the autosave preference to localStorage
 * @param enabled - true to enable, false to disable
 */
export function setAutosavePreference(enabled: boolean): void {
  try {
    localStorage.setItem(AUTOSAVE_STORAGE_KEY, String(enabled));
  } catch (error) {
    // localStorage might be unavailable (private browsing, storage full, etc.)
    console.warn(
      "[Autosave] Failed to save preference to localStorage:",
      error
    );
  }
}

/**
 * Clear the autosave preference (useful for testing/debugging)
 */
export function clearAutosavePreference(): void {
  try {
    localStorage.removeItem(AUTOSAVE_STORAGE_KEY);
  } catch (error) {
    console.warn(
      "[Autosave] Failed to clear preference from localStorage:",
      error
    );
  }
}
